import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Send, Check, Zap, Target, Smartphone, Shield, Star } from 'lucide-react';
import { PageSEO } from '@/components/SEO';
import axios from 'axios';

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;

export default function Campaign() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    company: '', 
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);

  const validatePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    return digits.length === 8;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setPhoneError(null);

    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError('Telefonnummer må ha nøyaktig 8 siffer.');
      setIsSubmitting(false);
      return;
    }

    try {
      let recaptcha_token = null;
      if (window.grecaptcha) {
        try {
          recaptcha_token = await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
              window.grecaptcha.execute('6LdMuo0sAAAAAJGFT2Wi257JosNQSehbrGsYtsXo', { action: 'campaign_submit' })
                .then(resolve)
                .catch(reject);
            });
          });
        } catch (recaptchaErr) {
          console.error('reCAPTCHA error:', recaptchaErr);
        }
      }

      const submissionData = {
        ...formData,
        _subject: `KAMPANJE_REDESIGN: Ny Nettside fra ${formData.name}`,
        'g-recaptcha-response': recaptcha_token
      };

      if (FORMSPREE_ID) {
        await axios.post(`https://formspree.io/f/${FORMSPREE_ID}`, submissionData);
        navigate('/takk');
      } else {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, submissionData);
        if (response.data?.status === 'success') navigate('/takk');
      }
    } catch (err) {
      setError('Noe gikk galt. Prøv igjen eller ring oss.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f8fafc] text-[#0f172a] font-sans selection:bg-blue-600 selection:text-white antialiased">
      <PageSEO
        title="Ny Nettside? | Profesjonelt Design | Side Kraft"
        path="/trenger-nettside"
        description="Få en moderne og rask nettside som konverterer besøkende til kunder."
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#0f172a] py-20 lg:py-40">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1]">
              Trenger din bedrift en <span className="text-blue-500">ny nettside?</span>
            </h1>
            <p className="mt-8 text-xl text-slate-300 max-w-lg leading-relaxed">
              Vi bygger lynraske, mobilvennlige og søkemotoroptimaliserte nettsider som konverterer besøkende til kunder. Profesjonell design som representerer din merkevare.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#skjema" className="px-8 py-5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg shadow-lg shadow-blue-900/20 transition-all text-center uppercase tracking-wider text-sm">
                Få et uforpliktende tilbud
              </a>
            </div>
          </div>
          <div className="relative lg:h-[500px] flex items-center justify-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="relative z-10 w-full">
              <img 
                src="/campaign/hero_v3.png" 
                alt="Moderne nettsidedesign" 
                className="rounded-2xl shadow-2xl border border-slate-700 max-w-full h-auto"
              />
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-blue-400/10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 bg-white" id="tjenester">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-bold text-[#0f172a]">Hvorfor velge oss?</h2>
            <p className="mt-6 text-slate-500 max-w-2xl mx-auto text-xl leading-relaxed">Vi fokuserer på de tre viktigste pilarene for en suksessfull nettside i dagens marked.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center p-10 rounded-3xl border border-slate-50 hover:shadow-2xl hover:border-blue-500/10 transition-all group">
              <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all">
                <Smartphone className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[#0f172a]">Mobilvennlig design</h3>
              <p className="text-slate-500 text-lg leading-relaxed">Dine kunder bruker mobilen mer enn noen gang. Vi sørger for at siden ser perfekt ut på alle skjermstørrelser.</p>
            </div>
            <div className="text-center p-10 rounded-3xl border border-slate-50 hover:shadow-2xl hover:border-blue-500/10 transition-all group">
              <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all">
                <Target className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[#0f172a]">SEO Optimalisert</h3>
              <p className="text-slate-500 text-lg leading-relaxed">Vi bygger siden med teknisk SEO i bunn slik at du blir funnet på Google når kundene dine leter etter deg.</p>
            </div>
            <div className="text-center p-10 rounded-3xl border border-slate-50 hover:shadow-2xl hover:border-blue-500/10 transition-all group">
              <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all">
                <Zap className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-[#0f172a]">Ekstrem hastighet</h3>
              <p className="text-slate-500 text-lg leading-relaxed">Vi bruker moderne teknologi som sikrer lynrask lastetid og høy sikkerhet for kundene dine.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Preview / Showcase */}
      <section className="py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4 text-center md:text-left">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-[#0f172a]">Vårt tidligere arbeid</h2>
              <p className="mt-4 text-slate-500 text-xl leading-relaxed">Et utvalg av ferske prosjekter levert av oss.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-1 gap-8">
            <div className="relative group overflow-hidden rounded-3xl shadow-[0_50px_100px_-20px_rgba(0,0,0,0.15)] border border-white">
              <img 
                src="/campaign/showcase_v3.png" 
                alt="Vårt arbeid" 
                className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-10">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-2xl md:text-[2.5rem] font-medium italic text-[#0f172a] mb-12 leading-relaxed">
            "Samarbeidet med Side Kraft har vært fantastisk. De forstod våre behov umiddelbart og leverte en nettside som overgikk våre forventninger."
          </p>
          <div>
            <p className="font-bold text-blue-600 text-xl">Thomas Hansen</p>
            <p className="text-slate-400 text-sm uppercase tracking-[0.3em] font-bold mt-2">Daglig Leder, Innovasjon Norge</p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="skjema" className="py-32 bg-[#f8fafc] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20">
            <div>
              <h2 className="font-black text-4xl lg:text-7xl text-[#0f172a] leading-[1] mb-8">
                KLAR FOR <br /> NESTE STEG?
              </h2>
              <p className="text-slate-500 text-xl leading-relaxed mb-12">
                Fyll ut skjemaet, så tar en av våre rådgivere kontakt med deg innen 24 timer for en uforpliktende samtale om ditt prosjekt.
              </p>
              <div className="space-y-6 pt-12 border-t border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center text-blue-600">
                    <Check className="h-7 w-7" />
                  </div>
                  <p className="font-bold text-[#0f172a] uppercase tracking-widest text-sm">Personlig oppfølging</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white rounded-full shadow-md flex items-center justify-center text-blue-600">
                    <Check className="h-7 w-7" />
                  </div>
                  <p className="font-bold text-[#0f172a] uppercase tracking-widest text-sm">Gratis strategisamtale</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 md:p-14 rounded-3xl shadow-2xl border border-slate-50">
              <form onSubmit={handleSubmit} className="space-y-12">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div>
                    <label className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mb-2 block">Fullt navn *</label>
                    <input
                      type="text"
                      className="w-full bg-transparent border-b border-slate-200 focus:border-blue-600 px-0 py-4 outline-none text-xl transition-all"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mb-2 block">E-post *</label>
                    <input
                      type="email"
                      className="w-full bg-transparent border-b border-slate-200 focus:border-blue-600 px-0 py-4 outline-none text-xl transition-all"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div>
                    <label className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mb-2 block">Telefon *</label>
                    <input
                      type="tel"
                      className={`w-full bg-transparent border-b px-0 py-4 outline-none text-xl transition-all ${phoneError ? 'border-red-500' : 'border-slate-200 focus:border-blue-600'}`}
                      required
                      value={formData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                        setFormData({...formData, phone: val});
                        setPhoneError(null);
                      }}
                      maxLength={8}
                    />
                    {phoneError && <p className="text-xs text-red-500 mt-2 font-bold uppercase">{phoneError}</p>}
                  </div>
                  <div>
                    <label className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mb-2 block">Bedrift</label>
                    <input
                      type="text"
                      className="w-full bg-transparent border-b border-slate-200 focus:border-blue-600 px-0 py-4 outline-none text-xl transition-all"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mb-2 block">Melding *</label>
                  <textarea
                    rows={4}
                    className="w-full bg-transparent border-b border-slate-200 focus:border-blue-600 px-0 py-4 outline-none text-xl resize-none transition-all"
                    required
                    placeholder="Fortell oss litt om ditt prosjekt..."
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-6 bg-[#0f172a] hover:bg-blue-600 text-white font-bold rounded-xl transition-all shadow-xl uppercase tracking-[0.3em] text-xs disabled:opacity-50"
                >
                  {isSubmitting ? 'Sender...' : 'Send forespørsel'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
